import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Loader2, Target, MapPin, Info, Search, Pin } from "lucide-react";
import { sr } from "../../lib/sr";
import TrendsPanel from "../../components/smartreach/TrendsPanel";
import CompetitorCompare from "../../components/smartreach/CompetitorCompare";

const escapeHtml = (s) =>
  String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Imperative Leaflet map (L.map + map.remove() cleanup) so React 18 StrictMode's
// mount→cleanup→mount double-invoke re-inits cleanly — the declarative <MapContainer>
// reused the same DOM node and threw "Map container is already initialized".
function StudioMap({ active, competitors, reachMi }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const start = active ? [active.lat, active.lng] : [32.78, -96.79];
    const map = L.map(containerRef.current, { scrollWheelZoom: false }).setView(start, 14);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const reachM = Math.max(0, reachMi || 0) * 1609.34;
    if (active) {
      L.circleMarker([active.lat, active.lng], { radius: 10, color: "#F2A33D", fillColor: "#F2A33D", fillOpacity: 0.9, weight: 2 })
        .bindPopup(`Your location · ${escapeHtml(active.name)}`).addTo(layer);
    }
    const reachCircles = [];
    (competitors || []).forEach((c) => {
      const isPoly = c.boundary_type === "polygon";
      if (reachM > 0) {
        const rc = L.circle([c.lat, c.lng], { radius: reachM, color: "#2563EB", fillColor: "#2563EB", fillOpacity: 0.08, weight: 1.5, dashArray: "6" });
        rc.addTo(layer); reachCircles.push(rc);
      }
      L.circle([c.lat, c.lng], { radius: c.radius_m ?? 400, color: "#A33A3A", fillColor: "#A33A3A", fillOpacity: 0.18, weight: isPoly ? 2 : 1, dashArray: isPoly ? "4" : undefined }).addTo(layer);
      L.circleMarker([c.lat, c.lng], { radius: 6, color: "#A33A3A", fillColor: "#A33A3A", fillOpacity: 0.9, weight: 2 })
        .bindPopup(`${escapeHtml(c.name)}<br/>${isPoly ? "Custom polygon" : `Smallest radius ${c.radius_m ?? 400}m`}${reachM > 0 ? `<br/>Campaign reach ${(reachMi).toFixed(2)} mi` : ""}`).addTo(layer);
    });
    if (reachCircles.length) {
      try { map.fitBounds(L.featureGroup(reachCircles).getBounds().pad(0.15), { maxZoom: 15 }); }
      catch { if (active) map.setView([active.lat, active.lng], 14); }
    } else if (active) {
      map.setView([active.lat, active.lng], 14);
    }
    setTimeout(() => map.invalidateSize(), 0);
  }, [active?.id, active, competitors, reachMi]);

  return (
    <div className="sr-card" style={{ marginTop: 14, padding: 0, overflow: "hidden" }} data-testid="studio-map">
      <div ref={containerRef} style={{ height: 420, width: "100%" }} />
    </div>
  );
}

export default function Competitors() {
  const [locs, setLocs] = useState(null);
  const [active, setActive] = useState(null);
  const [twin, setTwin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [pinnedIds, setPinnedIds] = useState({});
  const [compareKey, setCompareKey] = useState(0);
  const [reachMi, setReachMi] = useState(1);

  useEffect(() => {
    sr.pinnedCompetitors().then((r) => {
      const m = {}; (r.pinned || []).forEach((p) => { m[p.place_id] = true; }); setPinnedIds(m);
    }).catch(() => {});
  }, []);

  const pin = async (c) => {
    try {
      await sr.pinCompetitor({ place_id: c.place_id, name: c.name, address: c.address });
      setPinnedIds((m) => ({ ...m, [c.place_id]: true }));
      setCompareKey((k) => k + 1);
      toast.success(`Pinned ${c.name} to compare`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not pin"); }
  };

  useEffect(() => {
    sr.locations().then((r) => {
      const verified = (r.locations || []).filter((l) => l.lat && l.lng);
      setLocs(verified);
      if (verified[0]) selectLoc(verified[0]);
    }).catch(() => setLocs([]));
    // eslint-disable-next-line
  }, []);

  const selectLoc = async (loc) => {
    setActive(loc);
    try { const t = await sr.marketTwin(loc.id); setTwin(t); } catch { setTwin(null); }
  };

  const discover = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await sr.discoverCompetitors(active.id, 5000);
      if (!r.configured) toast.message("Competitor discovery pending external activation");
      else toast.success(`${r.count} competitors discovered`);
      const t = await sr.marketTwin(active.id); setTwin(t);
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery failed"); } finally { setBusy(false); }
  };

  const findCustom = async () => {
    if (!active) return;
    const kw = (keyword || "").trim();
    if (!kw) { toast.error("Type what kind of competitor to find (e.g. Tax preparation)"); return; }
    setBusy(true);
    try {
      const r = await sr.discoverCompetitors(active.id, 5000, kw);
      if (!r.configured) toast.message("Competitor discovery pending external activation");
      else toast.success(`${r.count} “${kw}” competitor${r.count === 1 ? "" : "s"} found`);
      const t = await sr.marketTwin(active.id); setTwin(t);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); } finally { setBusy(false); }
  };

  if (!locs) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const competitors = (twin?.competitors || []).filter((c) => c.lat && c.lng);
  const reachArea = Math.round(competitors.length * Math.PI * reachMi * reachMi * 10) / 10;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 30 }}>Competitor Studio</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Target and measure around exact competitor storefronts — using custom boundaries or the smallest supported radius.</p>
        </div>
        {active && <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={discover} disabled={busy} data-testid="studio-discover-btn">
          {busy ? <Loader2 size={15} className="sr-spin" /> : <><Target size={15} /> Discover competitors</>}
        </button>}
      </div>

      {locs.length === 0 ? (
        <div className="sr-card sr-empty" style={{ marginTop: 18 }}>
          <MapPin size={26} style={{ opacity: .4 }} />
          <p style={{ marginTop: 10 }}>Add a verified location first — the studio maps competitors around it.</p>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {locs.map((l) => (
              <button key={l.id} className={`sr-badge ${active?.id === l.id ? "sr-badge-green" : "sr-badge-gray"}`}
                style={{ cursor: "pointer", border: "none" }} onClick={() => selectLoc(l)} data-testid={`studio-loc-${l.id}`}>{l.name}</button>
            ))}
          </div>

          {active && (
            <div className="sr-card" style={{ marginTop: 12 }} data-testid="studio-custom-search">
              <label className="sr-label" style={{ marginBottom: 6 }}>Not the right competitors? Search by type or name</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="sr-input" style={{ flex: 1 }} value={keyword} onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && findCustom()} placeholder="e.g. Tax preparation, Accountant, or a specific business name"
                  data-testid="studio-keyword-input" />
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={findCustom} disabled={busy} data-testid="studio-custom-btn">
                  {busy ? <Loader2 size={15} className="sr-spin" /> : <><Search size={15} /> Find</>}
                </button>
              </div>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                <Info size={11} /> A custom search replaces the mapped competitors for this location with the ones that match your keyword.
              </p>
            </div>
          )}

          {active && competitors.length > 0 && (
            <div className="sr-card" style={{ marginTop: 14 }} data-testid="reach-radius-control">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <label className="sr-label" style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 6 }}><Target size={14} /> Campaign reach radius</label>
                <strong style={{ color: "var(--navy)" }} data-testid="reach-radius-value">{reachMi.toFixed(2)} mi</strong>
              </div>
              <input type="range" min="0.25" max="3" step="0.25" value={reachMi} onChange={(e) => setReachMi(Number(e.target.value))}
                data-testid="reach-radius-slider" style={{ width: "100%", marginTop: 12, accentColor: "#2563EB" }} />
              <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 8 }} data-testid="reach-radius-summary">
                Your conquesting ads would target around <strong style={{ color: "var(--navy)" }}>{competitors.length}</strong> competitor{competitors.length === 1 ? "" : "s"} · ~<strong style={{ color: "var(--navy)" }}>{reachArea}</strong> sq mi covered at {reachMi.toFixed(2)} mi each (blue rings on the map).
              </p>
              <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 6 }}>
                <Info size={11} /> Coverage is approximate — overlaps between nearby competitors aren&apos;t deducted, and actual delivery depends on what your channels support in this area.
              </p>
            </div>
          )}

          <StudioMap active={active} competitors={competitors} reachMi={reachMi} />

          <div className="sr-card" style={{ marginTop: 14 }}>
            <strong style={{ color: "var(--navy)" }}>Mapped storefronts ({competitors.length})</strong>
            <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
              {competitors.length === 0 && <p className="sr-muted" style={{ fontSize: 14 }}>No competitors mapped yet — tap “Discover competitors” or search by type above.</p>}
              {competitors.map((c) => (
                <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 14, padding: "8px 0", borderBottom: "1px solid var(--border)" }} data-testid={`studio-comp-${c.id}`}>
                  <span>{c.name} <span className="sr-muted">· {c.address}</span></span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span className="sr-badge sr-badge-gray">{c.boundary_type === "polygon" ? "Custom polygon" : `Radius ${c.radius_m || 400}m`}</span>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => pin(c)} disabled={!c.place_id || pinnedIds[c.place_id]} data-testid={`studio-pin-${c.id}`} title="Pin to compare">
                      <Pin size={13} /> {pinnedIds[c.place_id] ? "Pinned" : "Pin"}
                    </button>
                  </span>
                </div>
              ))}
            </div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 12 }}>
              <Info size={11} /> We show the actual activated boundary — a custom polygon where supported, otherwise the smallest supported radius. We never claim universal 25-meter targeting.
            </p>
          </div>

          <CompetitorCompare key={compareKey} title="Saved competitors — rating &amp; review trend" />
        </>
      )}
      <TrendsPanel />
    </div>
  );
}
